import React, {useEffect, useState} from 'react';
import _ from 'underscore';

import {SampleDetails} from 'constants/SampleConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import ColumnSelector from 'notebook/sample-propagation/add-samples-modal/ColumnSelector';
import SourceSampleSelectionTable from 'notebook/sample-propagation/sample-selection/SourceSampleSelectionTable';
import getSampleDescriptor, {
  EMPTY_SAMPLE_DESCRIPTOR,
} from 'notebook/sample-propagation/util/getSampleDescriptor';
import {
  sampleFiltersToWorklistTypes,
  worklistTypeToSampleFilter,
} from 'notebook/sample-propagation/util/SampleTypeWorklistTypeConversions';
import {
  StructuredTableAddOperationOptions,
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {isTableLengthLocked} from 'notebook/structured-tables/util/isTableLengthLocked';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import {ValidateWorklistItems, Worklist, WorklistType} from 'worklists/WorklistConstants';
import WorklistSelector, {WorklistSelectorDetails} from 'worklists/WorklistSelector';

import {NO_DESTINATIONS_AVAILABLE, NO_SAMPLES_AVAILABLE} from './add-samples-modal/AddSamplesModalConstants';
import nonExpansionFooterMessage from './add-samples-modal/nonExpansionFooterMessage';
import {SampleFilter, sampleFilterToIcon} from './SampleFilter';
import useHydrateWorklistSamples from './useHydrateWorklistSamples';
import {maximumNumberOfSamplesThatCanBeAddedBeforeExpansion} from './util/addSamplesToTable';
import getSampleId from './util/getSampleId';
import makeSampleDetailsFromSampleIds from './util/makeSampleDetailsFromSampleIds';

interface AddSamplesFromWorklistModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (addSamplesCommand: {
    worklist: Worklist;
    destinationColumnsCids: Array<string> | null;
  }) => void;
  worklistTypes: Array<WorklistType>;
  // When the destination is another structured table, you must provide a list of
  // {sample filter, destination column array, name displayed in modal} objects.
  destinationSampleFiltersByColumn?: Array<{
    sampleFilters: Array<SampleFilter>;
    columns: Array<StructuredTableColumn>;
    columnIndices?: Array<number>;
    destinationDisplayName: string;
  }>;
  validateItems?: ValidateWorklistItems;
  // Otherwise, you must provide a sampleFilter, indicating what you will accept
  destinationSampleType?: SampleFilter | null;
  destinationTable?: UnknownStructuredTable;
  options?: StructuredTableAddOperationOptions;
}

export interface SampleWithRowCount {
  sample: SampleDetails;
  rowCount: number;
}

interface WorklistStatusMessageProps {
  numberOfUsableSamples: number;
  numberOfUnusableSamples: number;
  destinationColumnName: string | null;
}

function WorklistStatusMessage({
  numberOfUsableSamples,
  numberOfUnusableSamples,
  destinationColumnName,
}: WorklistStatusMessageProps): JSX.Element | null {
  let errorMessage: string | null = null;

  if (numberOfUsableSamples === 0) {
    if (numberOfUnusableSamples === 0) {
      errorMessage = 'The selected worklist is empty.';
    } else {
      errorMessage = `None of the ${numberOfUnusableSamples} samples in this worklist are
       available and compatible with the ${
         destinationColumnName ? `${destinationColumnName} column.` : 'selected destination.'
       }`;
    }
  } else if (numberOfUnusableSamples > 0) {
    errorMessage = `${numberOfUnusableSamples} ${
      numberOfUnusableSamples > 1 ? 'samples were' : 'sample was'
    } hidden because ${numberOfUnusableSamples > 1 ? 'they are ' : 'it is '}
    unavailable${destinationColumnName ? ` or incompatible with the ${destinationColumnName} column.` : '.'}`;
  }

  return errorMessage ? <p>{errorMessage}</p> : null;
}

export default function AddSamplesFromWorklistModal(props: AddSamplesFromWorklistModalProps): JSX.Element {
  const {destinationSampleFiltersByColumn} = props;
  const [details, setDetails] = useState<WorklistSelectorDetails | null>(null);
  const [validIds, setValidIds] = useState<Array<string>>([]);
  const [unusableIds, setUnusableIds] = useState<Array<string>>([]);
  const [samplesWithSchemaMismatch, setSamplesWithSchemaMismatch] = useState<Array<string>>([]);
  const [destinationColumnIndex, setDestinationColumnIndex] = useState<number | null>(
    destinationSampleFiltersByColumn?.length === 1 ? 0 : null
  );
  const [destinationSchemaId, setDestinationSchemaId] = useState<string | null | undefined>();
  const [samplesWithRowCounts, setSamplesWithRowCounts] = useState<Array<SampleWithRowCount> | null>(null);
  const selectedDestination =
    destinationColumnIndex == null ? undefined : destinationSampleFiltersByColumn?.[destinationColumnIndex];
  const primarySampleFilter =
    selectedDestination?.sampleFilters.length === 1 ? selectedDestination.sampleFilters[0] : null;
  const validWorklistTypes =
    primarySampleFilter != null ? sampleFiltersToWorklistTypes([primarySampleFilter]) : props.worklistTypes;
  const descriptor = getSampleDescriptor(
    props.destinationSampleType ?? (destinationColumnIndex != null ? primarySampleFilter : null)
  );
  // The SourceSampleSelectionTable takes an array of full SampleDetails objects. However, worklists only
  // store the IDs of their contents. We query the server to ensure that the sample _names_ are in the store,
  // so we can display them to the user in the table.
  const {status: hydrationState, schemaIdBySampleId} = useHydrateWorklistSamples(
    // We attempt to hydrate all samples in the worklist; not filtered in any way
    details?.worklist?.itemIds ?? []
  );
  const preventTableExpansion =
    (isTableLengthLocked(props.destinationTable) || props.options?.preventTableExpansion) ?? false;
  const preventTableExpansionMessage = props.options?.preventTableExpansionMessage;

  const itemIdsToSubmit: Array<string> = [];
  if (samplesWithRowCounts != null) {
    for (const {sample, rowCount} of samplesWithRowCounts) {
      const rowSampleIds = Array(rowCount).fill(getSampleId(sample));
      itemIdsToSubmit.push(...rowSampleIds);
    }
  }

  // Update the destination schema id when the destination column changes
  useEffect(() => {
    setDestinationSchemaId(primarySampleFilter?.schemaId ?? null);
  }, [destinationSampleFiltersByColumn, destinationColumnIndex, primarySampleFilter?.schemaId]);

  // Update the list of samples that don't match the destination schema. Samples without a schema
  // are allowed through, and if no schema is present on the destination, all samples pass through
  useEffect(() => {
    setSamplesWithSchemaMismatch(
      destinationSchemaId == null
        ? []
        : (details?.worklist?.itemIds ?? []).filter((itemId) => {
            const sampleSchemaId = schemaIdBySampleId[itemId];
            return sampleSchemaId != null && sampleSchemaId !== destinationSchemaId;
          })
    );
  }, [details, destinationSchemaId, schemaIdBySampleId]);

  // Reassess samples for validity
  useEffect(() => {
    const worklist = details?.worklist;

    // Items must not be archived or inaccessible, and must have a matching (or null) schema
    const newValidIds =
      worklist == null
        ? []
        : _.difference(
            worklist.itemIds,
            worklist.archivedItemIds,
            worklist.inaccessibleItemIds,
            details!.badItemIds,
            samplesWithSchemaMismatch
          );

    setValidIds(newValidIds);
    setUnusableIds(_.difference(worklist?.itemIds ?? [], newValidIds));
  }, [details, samplesWithSchemaMismatch]);

  function handleSubmit(): void {
    props.onRequestSubmit({
      worklist: {...details!.worklist!, itemIds: itemIdsToSubmit},
      destinationColumnsCids:
        destinationSampleFiltersByColumn && destinationColumnIndex != null
          ? destinationSampleFiltersByColumn[destinationColumnIndex].columns.map((col) => col.cid)
          : null,
    });
  }

  const columnIndices = selectedDestination?.columnIndices;
  const nonExpansionModalFooter =
    preventTableExpansion &&
    destinationSampleFiltersByColumn &&
    props.destinationTable &&
    columnIndices != null &&
    destinationColumnIndex != null
      ? nonExpansionFooterMessage({
          numberOfSelectedSamples: itemIdsToSubmit.length,
          maximumNumberOfSamplesBeforeExpansion: maximumNumberOfSamplesThatCanBeAddedBeforeExpansion(
            props.destinationTable,
            columnIndices
          ),
          preventTableExpansionMessage:
            preventTableExpansionMessage || 'This table cannot expand with new rows.',
          sampleDescriptor: descriptor,
        })
      : null;

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      title={`Add ${descriptor.plural} from worklist`}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          submit: true,
          text: details?.isValidating
            ? 'Validating worklist...'
            : `Add ${descriptor.plural}${itemIdsToSubmit.length > 0 ? ` (${itemIdsToSubmit.length})` : ''}`,
          disabled:
            details?.worklist == null || itemIdsToSubmit.length === 0 || nonExpansionModalFooter != null,
          style: ButtonStyle.SECONDARY,
          dataTestAttrs: {component: 'AddSampleFromWorklistModal', element: 'submitButton'},
        },
      ]}
      footer={nonExpansionModalFooter}
    >
      {destinationSampleFiltersByColumn?.length === 0 ? (
        <p>{NO_DESTINATIONS_AVAILABLE}</p>
      ) : validWorklistTypes.length === 0 ? (
        <p>{NO_SAMPLES_AVAILABLE}</p>
      ) : (
        <>
          <div className="u-small-spacing-bottom">
            {destinationSampleFiltersByColumn && (
              <ColumnSelector
                label="Destination column"
                columns={destinationSampleFiltersByColumn.map((destinationInfo) => ({
                  name: destinationInfo.destinationDisplayName,
                  icon: sampleFilterToIcon(destinationInfo.sampleFilters[0]),
                }))}
                onChange={setDestinationColumnIndex}
                selectedColumnIndex={destinationColumnIndex}
              />
            )}
          </div>
          <WorklistSelector
            disabled={destinationSampleFiltersByColumn && destinationColumnIndex == null}
            onChange={setDetails}
            worklistTypes={validWorklistTypes}
            validateItems={props.validateItems}
          />
          {details?.worklist &&
            (destinationColumnIndex != null || destinationSampleFiltersByColumn == null) && (
              <WorklistStatusMessage
                numberOfUsableSamples={validIds.length}
                numberOfUnusableSamples={unusableIds.length}
                destinationColumnName={selectedDestination?.destinationDisplayName ?? null}
              />
            )}
          {hydrationState === QueryResultType.SUCCESS ? (
            <SourceSampleSelectionTable
              sourceKey={details?.worklist?.id ?? 'no-worklist'}
              samples={makeSampleDetailsFromSampleIds(validIds)}
              sampleDescriptor={
                details?.worklist
                  ? getSampleDescriptor(worklistTypeToSampleFilter(details.worklist.itemType))
                  : EMPTY_SAMPLE_DESCRIPTOR
              }
              onChangeSelection={(newSamplesWithRowCounts) => {
                setSamplesWithRowCounts(newSamplesWithRowCounts);
              }}
            />
          ) : (
            <CenteredHorizontalThrobber />
          )}
        </>
      )}
    </Modal>
  );
}
